import Styles from './Message.module.scss'
import cx from 'classnames'
import { MessageContent } from './Content/MessageContent'
import { MessageProps, PollingMessageProps } from './Types'
import { usePollMessage } from 'hooks/queries/chat/usePollMessage'
import { AIJobStatus } from 'types'
import { ErrorMessage } from './ErrorMessage'
import { LastMessageActions } from './LastMessageActions'
import { LastMessageActionsV2 } from './LastMessageActionsV2'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const Message = ({ message, isLastMessage }: MessageProps) => {
  const isChatV2 = useFlipperFlag('chat_v2')
  const isProcessing = message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status)
  let content = message.content

  if (isProcessing && content.length === 0) {
    content = [...content, { type: 'processing' }]
  }

  if (!content.length) return null

  const imageContent = content.filter((c) => c.type === 'image')
  const otherContent = content.filter((c) => c.type !== 'image')
  const hasMixedContent = message.role === 'user' && imageContent.length > 0 && otherContent.length > 0

  if (hasMixedContent) {
    return (
      <div className={Styles.splitMessage} data-testid={`message-${message.id}`}>
        <div className={Styles.message}>
          {imageContent.map((item, id) => (
            <MessageContent key={`${message.id}-img-${id}`} content={item} message={message} />
          ))}
        </div>
        <div className={cx(Styles.message, Styles[message.role])}>
          {otherContent.map((item, id) => (
            <MessageContent key={`${message.id}-${id}`} content={item} message={message} />
          ))}
        </div>
      </div>
    )
  }

  const showLastMessageActions =
    !isProcessing &&
    isLastMessage &&
    message.role === 'assistant' &&
    !message.content.some((content) => content.type === 'unsupported_request')

  return (
    <div className={cx(Styles.message, Styles[message.role])} data-testid={`message-${message.id}`}>
      {content.map((item, id) => (
        <MessageContent key={`${message.id}-${id}`} content={item} message={message} />
      ))}
      {showLastMessageActions &&
        (isChatV2 ? <LastMessageActionsV2 message={message} /> : <LastMessageActions message={message} />)}
    </div>
  )
}

const PollingMessage = ({ threadId, id, isLastMessage }: PollingMessageProps) => {
  const { data: messageData } = usePollMessage({ threadId, id })

  if (!messageData) {
    return null
  }

  return <Message message={messageData.message} isLastMessage={isLastMessage} />
}

const MessageContainer = ({ message, isLastMessage }: MessageProps) => {
  if (message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status)) {
    return <PollingMessage threadId={message.thread_id} id={message.id} />
  }

  if (message.status === AIJobStatus.Failed) {
    return <ErrorMessage message={message} />
  }

  return <Message message={message} isLastMessage={isLastMessage} />
}

export { MessageContainer as Message }
